import React, { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Box,
  Typography,
  Button,
  Pagination,
  Skeleton,
  CardContent,
  Card,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Fade,
  Collapse,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import {
  SentimentDissatisfied as SadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Sort as SortIcon,
  FilterList as FilterIcon,
  Restaurant as RestaurantIcon,
  Timer as TimerIcon,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  Visibility as ViewIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  TrendingUp as TrendingIcon,
  Schedule as ScheduleIcon,
  LocalDining as DiningIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import HomePageRecipeCard from "./Cards/HomePageRecipeCard";

// Enhanced loading skeleton with better animations
const LoadingSkeleton = React.memo(() => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        bgcolor: "background.paper",
        height: "100%",
        minHeight: 400,
      }}
    >
      <motion.div
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        <Skeleton
          variant="rectangular"
          height={250}
          sx={{
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(90deg, ${theme.palette.grey[800]} 0%, ${theme.palette.grey[700]} 50%, ${theme.palette.grey[800]} 100%)`
                : `linear-gradient(90deg, ${theme.palette.grey[300]} 0%, ${theme.palette.grey[200]} 50%, ${theme.palette.grey[300]} 100%)`,
            backgroundSize: "200% 100%",
          }}
        />
      </motion.div>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width="70%" height={28} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" width={60} height={24} />
          ))}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Skeleton variant="text" width={100} height={20} />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={40} height={20} />
          </Box>
        </Box>
        <Skeleton variant="rounded" width="100%" height={36} />
      </CardContent>
    </Card>
  );
});

// Enhanced empty state with better animations
const EmptyState = React.memo(
  ({ onRefresh, onCreateNew, searchTerm, totalRecipes }) => {
    const theme = useTheme();
    const navigate = useNavigate();

    const getEmptyMessage = () => {
      if (searchTerm) {
        return {
          title: `No recipes found for "${searchTerm}"`,
          subtitle: "Try adjusting your search terms or browse our collection",
          showCreate: false,
        };
      }
      return {
        title: "No recipes available",
        subtitle: "Start building your recipe collection",
        showCreate: true,
      };
    };

    const { title, subtitle, showCreate } = getEmptyMessage();

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            p: 4,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
              borderRadius: 4,
              boxShadow: `0 12px 40px ${
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.4)"
                  : "rgba(0,0,0,0.1)"
              }`,
              p: 6,
              maxWidth: 500,
              textAlign: "center",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <motion.div
              animate={{
                rotate: [0, -10, 10, -10, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 3,
              }}
            >
              <SadIcon
                sx={{
                  fontSize: 80,
                  color: theme.palette.primary.main,
                  mb: 3,
                  filter: `drop-shadow(0 4px 8px ${theme.palette.primary.main}40)`,
                }}
              />
            </motion.div>

            <Typography
              variant="h4"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 4, opacity: 0.8 }}
            >
              {subtitle}
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              justifyContent="center"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={onRefresh}
                  sx={{
                    textTransform: "none",
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                    "&:hover": {
                      background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                      boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                    },
                  }}
                >
                  Refresh Recipes
                </Button>
              </motion.div>

              {showCreate && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/create-recipe")}
                    sx={{
                      textTransform: "none",
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      borderWidth: 2,
                      "&:hover": {
                        borderWidth: 2,
                        background: `${theme.palette.primary.main}10`,
                      },
                    }}
                  >
                    Create Recipe
                  </Button>
                </motion.div>
              )}
            </Stack>

            {searchTerm && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Box
                  sx={{ mt: 4, p: 2, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    💡 Try searching for: "pasta", "chicken", "dessert", or
                    "healthy"
                  </Typography>
                </Box>
              </motion.div>
            )}
          </Paper>
        </Box>
      </motion.div>
    );
  }
);

// Enhanced toolbar component - Removed view mode toggles
const RecipeToolbar = ({
  sortBy,
  onSortChange,
  showFilters,
  onToggleFilters,
  totalRecipes,
  currentRecipes,
  loading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const sortOptions = [
    { value: "newest", label: "Newest", icon: ScheduleIcon },
    { value: "popular", label: "Popular", icon: TrendingIcon },
    { value: "rating", label: "Top Rated", icon: StarIcon },
    { value: "name", label: "A-Z", icon: SortIcon },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
              : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack
          direction={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isMobile ? "stretch" : "center"}
          spacing={2}
        >
          {/* Results count */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RestaurantIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {loading ? (
                <Skeleton width={150} />
              ) : (
                `${currentRecipes} ${
                  currentRecipes === 1 ? "Recipe" : "Recipes"
                }`
              )}
            </Typography>
            {totalRecipes > 0 && totalRecipes !== currentRecipes && (
              <Chip
                label={`of ${totalRecipes}`}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Box>

          {/* Controls */}
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={2}
            alignItems={isMobile ? "stretch" : "center"}
          >
            {/* Sort dropdown */}
            <Box sx={{ minWidth: isMobile ? "100%" : 200 }}>
              <ToggleButtonGroup
                value={sortBy}
                exclusive
                onChange={(e, value) => value && onSortChange(value)}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  },
                }}
              >
                {sortOptions.map((option) => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: isMobile ? "auto" : 80,
                    }}
                  >
                    <option.icon sx={{ fontSize: 16 }} />
                    {!isMobile && option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Filter toggle - Fixed to be stable */}
            <Tooltip title="Toggle Filters">
              <IconButton
                onClick={onToggleFilters}
                color={showFilters ? "primary" : "default"}
                sx={{
                  backgroundColor: showFilters
                    ? `${theme.palette.primary.main}20`
                    : "transparent",
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.main}30`,
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <FilterIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>
    </motion.div>
  );
};

// Main component with enhanced features
const ListOfRecipes = ({
  loading = false,
  error = null,
  currentRecipes = [],
  handlePageChange,
  currentPage = 1,
  totalPages = 1,
  refetch,
  filters = {},
  onFilterChange = () => {},
  searchTerm = "",
  totalRecipes = 0,
  isSearchActive = false,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Enhanced sorting logic
  const sortedRecipes = useMemo(() => {
    if (!currentRecipes.length) return [];

    const sorted = [...currentRecipes].sort((a, b) => {
      switch (sortBy) {
        case "popular":
          return (b.viewsCount || 0) - (a.viewsCount || 0);
        case "rating":
          return (b.averageRating || 0) - (a.averageRating || 0);
        case "name":
          return (a.recipe_name || "").localeCompare(b.recipe_name || "");
        case "newest":
        default:
          return new Date(b.created || 0) - new Date(a.created || 0);
      }
    });

    return sorted;
  }, [currentRecipes, sortBy]);

  // Fixed grid configuration - always use consistent grid layout
  const getGridProps = useCallback(() => {
    return {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 3,
    };
  }, []);

  // Handle sort change
  const handleSortChange = useCallback((newSort) => {
    setSortBy(newSort);
  }, []);

  // Handle filter toggle with fixed width
  const handleToggleFilters = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 100,
      },
    },
  };

  // Loading state
  if (loading && !currentRecipes.length) {
    return (
      <Box sx={{ width: "100%" }}>
        <RecipeToolbar
          sortBy={sortBy}
          onSortChange={handleSortChange}
          showFilters={showFilters}
          onToggleFilters={handleToggleFilters}
          totalRecipes={0}
          currentRecipes={0}
          loading={true}
        />

        <Grid container spacing={3} sx={{ padding: 2 }}>
          {Array.from({ length: isMobile ? 4 : 8 }).map((_, index) => (
            <Grid item {...getGridProps()} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <LoadingSkeleton />
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Error state
  if (error && !currentRecipes.length) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Paper
          elevation={4}
          sx={{
            p: 4,
            borderRadius: 4,
            textAlign: "center",
            border: `2px solid ${theme.palette.error.main}30`,
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.error.dark}10 0%, ${theme.palette.background.paper} 100%)`
                : `linear-gradient(135deg, ${theme.palette.error.light}10 0%, ${theme.palette.background.paper} 100%)`,
          }}
        >
          <Typography variant="h5" color="error" gutterBottom fontWeight={700}>
            Oops! Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error.message || "Failed to load recipes. Please try again."}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
            color="error"
          >
            Retry
          </Button>
        </Paper>
      </motion.div>
    );
  }

  // Empty state
  if (!sortedRecipes.length) {
    return (
      <Box sx={{ width: "100%" }}>
        <RecipeToolbar
          sortBy={sortBy}
          onSortChange={handleSortChange}
          showFilters={showFilters}
          onToggleFilters={handleToggleFilters}
          totalRecipes={totalRecipes}
          currentRecipes={0}
          loading={false}
        />

        <EmptyState
          onRefresh={() => window.location.reload()}
          onCreateNew={() => navigate("/create-recipe")}
          searchTerm={searchTerm}
          totalRecipes={totalRecipes}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Enhanced toolbar */}
      <RecipeToolbar
        sortBy={sortBy}
        onSortChange={handleSortChange}
        showFilters={showFilters}
        onToggleFilters={handleToggleFilters}
        totalRecipes={totalRecipes}
        currentRecipes={sortedRecipes.length}
        loading={loading}
      />

      {/* Fixed width filter panel to prevent layout shifts */}
      <Box sx={{ minHeight: showFilters ? "auto" : 0, overflow: "hidden" }}>
        <Collapse in={showFilters}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Filter Recipes
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {[
                "Vegetarian",
                "Quick",
                "Healthy",
                "Desserts",
                "Main Course",
              ].map((filter) => (
                <Chip
                  key={filter}
                  label={filter}
                  variant="outlined"
                  clickable
                  sx={{
                    "&:hover": {
                      backgroundColor: `${theme.palette.primary.main}20`,
                    },
                  }}
                />
              ))}
            </Stack>
          </Paper>
        </Collapse>
      </Box>

      {/* Recipe grid with enhanced animations */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Grid container spacing={3} sx={{ padding: 2, minHeight: "60vh" }}>
          <AnimatePresence mode="wait">
            {sortedRecipes.map((recipe, index) => (
              <Grid item {...getGridProps()} key={recipe.id}>
                <motion.div
                  variants={itemVariants}
                  layout
                  whileHover={{
                    y: -8,
                    transition: { duration: 0.2 },
                  }}
                  onHoverStart={() => setHoveredCard(recipe.id)}
                  onHoverEnd={() => setHoveredCard(null)}
                  style={{
                    height: "100%",
                  }}
                >
                  <HomePageRecipeCard
                    recipe={recipe}
                    refresh={refetch}
                    navigationPath="recipe"
                    isHovered={hoveredCard === recipe.id}
                  />
                </motion.div>
              </Grid>
            ))}
          </AnimatePresence>
        </Grid>
      </motion.div>

      {/* Enhanced pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            my={6}
            sx={{
              p: 3,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Page {currentPage} of {totalPages}
              </Typography>

              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: 2,
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: `${theme.palette.primary.main}20`,
                    },
                    "&.Mui-selected": {
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      color: "white",
                      "&:hover": {
                        background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                      },
                    },
                  },
                }}
              />

              <Typography variant="body2" color="text.secondary">
                {sortedRecipes.length} recipes shown
              </Typography>
            </Stack>
          </Box>
        </motion.div>
      )}

      {/* Loading overlay for additional states */}
      <AnimatePresence>
        {loading && currentRecipes.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.3)",
              backdropFilter: "blur(4px)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              style={{
                background: theme.palette.background.paper,
                borderRadius: "16px",
                padding: "24px 32px",
                boxShadow: `0 20px 60px rgba(0,0,0,0.3)`,
                display: "flex",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                style={{
                  width: "24px",
                  height: "24px",
                  border: `3px solid ${theme.palette.primary.main}30`,
                  borderTop: `3px solid ${theme.palette.primary.main}`,
                  borderRadius: "50%",
                }}
              />
              <Typography variant="body1" fontWeight={600}>
                Updating recipes...
              </Typography>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

ListOfRecipes.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  currentRecipes: PropTypes.array,
  handlePageChange: PropTypes.func,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  refetch: PropTypes.func,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func,
  searchTerm: PropTypes.string,
  totalRecipes: PropTypes.number,
  isSearchActive: PropTypes.bool,
};

export default React.memo(ListOfRecipes);
